/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as SoundActionCreators from '@app/actions/SoundActionCreators';
import {getStreamKey} from '@app/components/voice/StreamKeys';
import {Logger} from '@app/lib/Logger';
import {Platform} from '@app/lib/Platform';
import LocalVoiceStateStore from '@app/stores/LocalVoiceStateStore';
import MediaPermissionStore from '@app/stores/MediaPermissionStore';
import VoiceSettingsStore from '@app/stores/VoiceSettingsStore';
import VoiceConnectionManager from '@app/stores/voice/VoiceConnectionManager';
import {syncLocalVoiceStateWithServer, updateLocalParticipantFromRoom} from '@app/stores/voice/VoiceMediaEngineBridge';
import VoiceMediaStateCoordinator from '@app/stores/voice/VoiceMediaStateCoordinator';
import {
	type AppAudioCapture,
	type AppAudioSpec,
	createAppAudioTrack,
	supportsPerAppAudioCapture,
	supportsSystemExcludeAudioCapture,
} from '@app/utils/AppAudioCapture';
import {ScreenRecordingPermissionDeniedError} from '@app/utils/errors/ScreenRecordingPermissionDeniedError';
import {ensureNativePermission} from '@app/utils/NativePermissions';
import {isDesktop, isNativeMacOS, supportsDesktopScreenShareAudioCapture} from '@app/utils/NativeUtils';
import {SoundType} from '@app/utils/SoundUtils';
import {
	type Room,
	type ScreenShareCaptureOptions,
	Track,
	type TrackPublishOptions,
	type VideoCodec,
} from 'livekit-client';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('VoiceScreenShareManager');

function getPreferredScreenShareCodec(): VideoCodec {
	// Driven by the in-app setting (Settings -> Advanced -> Screen Sharing).
	// h264 = hardware (NVENC) and best for stable fps; vp9 = software; h265 = experimental.
	return VoiceSettingsStore.getScreenShareCodec();
}

class VoiceScreenShareManager {
	isScreenSharePending = false;

	// Set by the desktop source picker when native stream-audio applies for the next share: the shared
	// window's PID (or null for a whole screen). The actual capture mode (include the window vs exclude
	// Fluxer) is derived from the audio-source setting at publish time. Consumed once.
	private pendingNativeAudio: {windowProcessId: string | null} | null = null;
	// The shared window's PID while streaming (null = whole screen) — kept so a live source-mode switch
	// can re-derive include/exclude without re-picking.
	private activeSharedWindowProcessId: string | null = null;
	// The live native (WASAPI process-loopback) audio capture, if any. Held so we can stop the native
	// helper + close the generator on teardown.
	private appAudioCapture: AppAudioCapture | null = null;

	constructor() {
		makeAutoObservable(
			this,
			{pendingNativeAudio: false, activeSharedWindowProcessId: false, appAudioCapture: false},
			{autoBind: true},
		);
	}

	// Called by the screen-share source picker (desktop) to register that the next share should use native
	// stream audio, with the shared window's PID (or null for a whole screen). Pass null to use the regular
	// (loopback) audio path instead.
	setPendingScreenShareAudioSource(info: {windowProcessId: string | null} | null): void {
		this.pendingNativeAudio = info;
	}

	private consumePendingNativeAudio(): {windowProcessId: string | null} | null {
		const pending = this.pendingNativeAudio;
		this.pendingNativeAudio = null;
		return pending;
	}

	// Resolve which native capture to use from the current audio-source setting + the shared window.
	// Returns null when no native capture is available (web, or older desktop without exclude support).
	private computeAudioSpec(windowProcessId: string | null): AppAudioSpec | null {
		const mode = VoiceSettingsStore.getScreenShareAudioSource();
		if (mode === 'application' && windowProcessId && supportsPerAppAudioCapture()) {
			return {mode: 'include', processId: windowProcessId};
		}
		if (supportsSystemExcludeAudioCapture()) {
			return {mode: 'exclude'};
		}
		return null;
	}

	getIsScreenSharePending(): boolean {
		return this.isScreenSharePending;
	}

	private getLocalStreamKey(): string {
		const {guildId, channelId, connectionId} = VoiceConnectionManager.connectionState;
		if (!connectionId) {
			const error = new Error('Cannot sync screen share watcher without an active voice connection');
			logger.error('Missing connection id when deriving local stream key', {
				connectionState: VoiceConnectionManager.connectionState,
			});
			throw error;
		}
		return getStreamKey(guildId, channelId, connectionId);
	}

	private syncLocalStreamWatchState(enabled: boolean): void {
		const streamKey = this.getLocalStreamKey();
		const current = LocalVoiceStateStore.getViewerStreamKeys();
		if (enabled) {
			if (current.includes(streamKey)) return;
			const updated = [...current, streamKey];
			LocalVoiceStateStore.updateViewerStreamKeys(updated);
			syncLocalVoiceStateWithServer({viewer_stream_keys: updated});
			return;
		}

		if (!current.includes(streamKey)) {
			logger.error('Viewer stream key not found in array while disabling screen share', {
				current,
				expected: streamKey,
			});
			const updated = current.filter((k) => k !== streamKey);
			LocalVoiceStateStore.updateViewerStreamKeys(updated);
			syncLocalVoiceStateWithServer({viewer_stream_keys: updated});
			return;
		}
		const updated = current.filter((k) => k !== streamKey);
		LocalVoiceStateStore.updateViewerStreamKeys(updated);
		syncLocalVoiceStateWithServer({viewer_stream_keys: updated});
	}

	async setScreenShareEnabled(
		room: Room | null,
		enabled: boolean,
		options?: ScreenShareCaptureOptions & {
			sendUpdate?: boolean;
			playSound?: boolean;
			restartIfEnabled?: boolean;
		},
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share not supported on native');
			return;
		}

		const {sendUpdate = true, playSound = true, restartIfEnabled = false, ...restOptions} = options || {};
		const participant = room?.localParticipant;

		if (!participant) {
			logger.warn('No participant');
			return;
		}

		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring request');
			return;
		}

		if (enabled && restartIfEnabled && participant.isScreenShareEnabled) {
			await this.setScreenShareEnabled(room, false, {
				sendUpdate: false,
				playSound: false,
			});
			await this.setScreenShareEnabled(
				room,
				true,
				{
					...restOptions,
					sendUpdate,
					playSound,
				},
				publishOptions,
			);
			return;
		}

		if (enabled && isDesktop() && isNativeMacOS()) {
			const denied = MediaPermissionStore.isScreenRecordingExplicitlyDenied();
			if (denied) {
				logger.warn('Screen recording permission explicitly denied');
				throw new ScreenRecordingPermissionDeniedError();
			}

			const nativeResult = await ensureNativePermission('screen');
			if (nativeResult === 'denied') {
				logger.warn('Screen recording permission denied');
				MediaPermissionStore.markScreenRecordingExplicitlyDenied();
				throw new ScreenRecordingPermissionDeniedError();
			}

			if (nativeResult === 'granted') {
				MediaPermissionStore.updateScreenRecordingPermissionGranted();
			}
		}

		const stateReason = sendUpdate ? 'user' : 'server';
		const applyState = (value: boolean) => {
			VoiceMediaStateCoordinator.applyScreenShareState(value, {reason: stateReason, sendUpdate});
		};

		if (!enabled) applyState(false);

		runInAction(() => {
			this.isScreenSharePending = true;
		});

		const preferredVideoCodec = publishOptions?.videoCodec ?? getPreferredScreenShareCodec();
		const effectivePublishOptions = enabled
			? {
					...publishOptions,
					videoCodec: preferredVideoCodec,
				}
			: publishOptions;

		try {
			// System/game audio must be captured RAW — mic DSP (echo-cancel/noise-suppress/AGC)
			// mangles music & game audio into a choppy mess. contentHint=motion keeps the framerate
			// smooth for gameplay (encoder drops detail, not frames, under load).
			// echoCancellation MUST stay on: for screen-audio capture it makes the browser subtract the
			// voice-call playout (other people's voices) from the captured system audio — without it,
			// remote participants hear themselves echoed back through the stream. noiseSuppression + AGC
			// stay OFF — those are the ones that mangle music/game audio into a choppy mess.
			const screenAudioCapture = {echoCancellation: true, noiseSuppression: false, autoGainControl: false};
			const enhancedScreenCapture = enabled
				? {
						...restOptions,
						contentHint: restOptions.contentHint ?? 'motion',
						...(restOptions.audio ? {audio: screenAudioCapture} : {}),
					}
				: restOptions;
			// Screen-share audio publish: no DTX (don't cut continuous audio), RED for loss resilience,
			// high bitrate for music/game audio. Caller options still win (spread last).
			// Quality mode + max bitrate come from the in-app setting (no rebuild needed to tune).
			const degradationPref = VoiceSettingsStore.getScreenShareDegradation();
			const bitrateOverrideMbps = VoiceSettingsStore.getScreenShareBitrateMbps();
			const baseScreenEncoding = effectivePublishOptions?.screenShareEncoding;
			const overriddenScreenEncoding =
				baseScreenEncoding && bitrateOverrideMbps > 0
					? {...baseScreenEncoding, maxBitrate: bitrateOverrideMbps * 1_000_000}
					: baseScreenEncoding;
			const enhancedScreenPublish = enabled
				? {
						dtx: false,
						red: true,
						audioPreset: {maxBitrate: 256_000},
						...effectivePublishOptions,
						degradationPreference: degradationPref,
						...(overriddenScreenEncoding ? {screenShareEncoding: overriddenScreenEncoding} : {}),
					}
				: effectivePublishOptions;
			await participant.setScreenShareEnabled(enabled, enhancedScreenCapture, enhancedScreenPublish);
			if (enabled) applyState(true);

			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(enabled);

			// Native stream audio (Discord-style): if the picker flagged native audio, the video was published
			// without system loopback — capture the chosen app (include) or the whole system minus Fluxer
			// (exclude) and publish it as the ScreenShareAudio track. Otherwise the regular loopback path
			// above stands (web / older desktop).
			if (enabled) {
				const pendingNativeAudio = this.consumePendingNativeAudio();
				if (pendingNativeAudio) {
					this.activeSharedWindowProcessId = pendingNativeAudio.windowProcessId;
					const spec = this.computeAudioSpec(pendingNativeAudio.windowProcessId);
					if (spec) {
						await this.publishNativeAudio(room, spec);
					}
				}
			}

			if (playSound) {
				if (enabled) {
					SoundActionCreators.playSound(SoundType.ScreenShareStart);
				} else {
					SoundActionCreators.playSound(SoundType.ScreenShareStop);
				}
			}
			logger.info('Success', {enabled});
		} catch (e) {
			if (e instanceof Error && (e.name === 'AbortError' || e.name === 'NotAllowedError')) {
				logger.debug('User cancelled or permission denied', {name: e.name});
				const actual = participant.isScreenShareEnabled;
				applyState(actual);
				updateLocalParticipantFromRoom(room);
				this.syncLocalStreamWatchState(actual);
				return;
			}

			logger.error('Failed', {enabled, error: e});
			const actual = participant.isScreenShareEnabled;
			applyState(actual);
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(actual);

			if (playSound) {
				if (actual) {
					SoundActionCreators.playSound(SoundType.ScreenShareStart);
				} else {
					SoundActionCreators.playSound(SoundType.ScreenShareStop);
				}
			}
		} finally {
			runInAction(() => {
				this.isScreenSharePending = false;
			});
		}
	}

	async updateActiveScreenShareSettings(
		room: Room | null,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share updates are not supported on native');
			return false;
		}

		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) {
			return false;
		}

		const screenSharePublication = participant.getTrackPublication(Track.Source.ScreenShare);
		const screenShareTrack = screenSharePublication?.videoTrack;
		if (!screenShareTrack) {
			logger.warn('No active screen share track to update');
			return false;
		}

		let appliedAnySetting = false;

		const resolution = options?.resolution;
		if (resolution) {
			const nextConstraints: MediaTrackConstraints = {};
			if (resolution.width > 0) {
				nextConstraints.width = {ideal: resolution.width};
			}
			if (resolution.height > 0) {
				nextConstraints.height = {ideal: resolution.height};
			}
			if (resolution.frameRate !== undefined) {
				nextConstraints.frameRate = resolution.frameRate;
			}

			try {
				await screenShareTrack.mediaStreamTrack.applyConstraints(nextConstraints);
				appliedAnySetting = true;
			} catch (error) {
				logger.warn('Failed to update active screen share constraints', {
					error,
					resolution,
				});
			}
		}

		if (options?.contentHint) {
			screenShareTrack.mediaStreamTrack.contentHint = options.contentHint;
			appliedAnySetting = true;
		}

		const screenShareEncoding = publishOptions?.screenShareEncoding;
		if (screenShareEncoding) {
			const sender = screenShareTrack.sender;
			if (!sender) {
				logger.warn('No sender found for active screen share track');
			} else {
				const senderParameters = sender.getParameters();
				if (!senderParameters.encodings || senderParameters.encodings.length === 0) {
					logger.warn('No sender encodings available for active screen share track');
				} else {
					senderParameters.encodings = senderParameters.encodings.map((encoding) => ({
						...encoding,
						maxBitrate: screenShareEncoding.maxBitrate,
						maxFramerate: screenShareEncoding.maxFramerate ?? encoding.maxFramerate,
						priority: screenShareEncoding.priority ?? encoding.priority,
					}));
					try {
						await sender.setParameters(senderParameters);
						appliedAnySetting = true;
					} catch (error) {
						logger.warn('Failed to update active screen share sender parameters', {
							error,
							screenShareEncoding,
						});
					}
				}
			}
		}

		if (typeof options?.audio === 'boolean') {
			const screenShareAudioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
			if (screenShareAudioPublication) {
				try {
					if (options.audio) {
						await screenShareAudioPublication.unmute();
					} else {
						await screenShareAudioPublication.mute();
					}
					appliedAnySetting = true;
				} catch (error) {
					logger.warn('Failed to update active screen share audio state', {
						error,
						includeAudio: options.audio,
					});
				}
			} else if (options.audio) {
				logger.info('Cannot enable screen share audio without restarting screen share');
			}
		}

		updateLocalParticipantFromRoom(room);
		return appliedAnySetting;
	}

	// Switch the shared window/screen WITHOUT interrupting the stream: re-open the source picker
	// (desktop = custom desktopCapturer picker, browser = native getDisplayMedia) and hot-swap the
	// underlying MediaStreamTrack via replaceTrack (sender.replaceTrack, no renegotiation) — viewers
	// see no drop, just the content changes (like Discord's "change window").
	async changeScreenShareSource(room: Room | null): Promise<boolean> {
		if (Platform.OS !== 'web') {
			return false;
		}
		const participant = room?.localParticipant;
		if (!participant) {
			return false;
		}
		const screenSharePublication = participant.getTrackPublication(Track.Source.ScreenShare);
		const screenShareTrack = screenSharePublication?.videoTrack;
		if (!screenShareTrack) {
			logger.warn('No active screen share to switch source');
			return false;
		}

		const wantAudio = supportsDesktopScreenShareAudioCapture() && LocalVoiceStateStore.getSelfStreamAudio();
		const screenAudioCapture = {echoCancellation: true, noiseSuppression: false, autoGainControl: false};

		let newStream: MediaStream;
		try {
			newStream = await navigator.mediaDevices.getDisplayMedia({
				video: true,
				audio: wantAudio ? screenAudioCapture : false,
				...({selfBrowserSurface: 'include', systemAudio: 'include'} as Record<string, unknown>),
			} as DisplayMediaStreamOptions);
		} catch (error) {
			logger.debug('Screen share source switch cancelled', {
				name: error instanceof Error ? error.name : 'unknown',
			});
			return false;
		}

		const newVideoTrack = newStream.getVideoTracks()[0];
		if (!newVideoTrack) {
			newStream.getTracks().forEach((t) => t.stop());
			return false;
		}
		newVideoTrack.contentHint = 'motion';

		try {
			await screenShareTrack.replaceTrack(newVideoTrack);
		} catch (error) {
			logger.error('Failed to replace screen share track', {error});
			newStream.getTracks().forEach((t) => t.stop());
			return false;
		}

		// Native audio: if the picker flagged native audio for the new source, swap the capture so the audio
		// follows the new window/screen. getDisplayMedia returned video-only in that case, so there's no
		// system-audio track to handle below.
		const pendingNativeAudio = this.consumePendingNativeAudio();
		if (pendingNativeAudio) {
			this.activeSharedWindowProcessId = pendingNativeAudio.windowProcessId;
			newStream.getAudioTracks().forEach((track) => track.stop());
			const spec = this.computeAudioSpec(pendingNativeAudio.windowProcessId);
			if (spec) {
				await this.publishNativeAudio(room, spec);
			}
			logger.info('Switched screen share source (native audio)');
			return true;
		}

		const newAudioTrack = newStream.getAudioTracks()[0];
		const audioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		if (newAudioTrack && audioPublication?.audioTrack) {
			await audioPublication.audioTrack.replaceTrack(newAudioTrack).catch((error) => {
				logger.warn('Failed to replace screen share audio track', {error});
			});
		} else if (newAudioTrack) {
			newAudioTrack.stop();
		}

		logger.info('Switched screen share source');
		return true;
	}

	// Stop & unpublish any lingering screen-share AUDIO publication. When the captured window/source
	// closes, LiveKit unpublishes the video track but the separate ScreenShareAudio track keeps running
	// — on desktop that track is the SYSTEM loopback capture, which does NOT end when one window closes,
	// so viewers keep hearing audio after the picture is gone. Idempotent: a no-op when nothing is left.
	async stopScreenShareAudio(room: Room | null): Promise<void> {
		// Stop the per-app (process-loopback) capture if one is running — its native helper + track
		// generator must be torn down regardless of how the screen share ended.
		this.stopAppAudioCapture();
		const participant = room?.localParticipant;
		if (!participant) {
			return;
		}
		const audioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		const audioTrack = audioPublication?.audioTrack;
		if (!audioTrack) {
			return;
		}
		try {
			await participant.unpublishTrack(audioTrack);
			logger.info('Stopped lingering screen share audio track');
		} catch (error) {
			logger.warn('Failed to stop lingering screen share audio track', {error});
		}
	}

	// Capture stream audio natively (include the shared app, or exclude Fluxer for system audio) and
	// publish it as the ScreenShareAudio track. No-op-with-warning if capture is unavailable so the share
	// still works (just without audio).
	private async publishNativeAudio(room: Room | null, spec: AppAudioSpec): Promise<void> {
		const participant = room?.localParticipant;
		if (!participant) {
			return;
		}
		// Tear down any existing screen-share audio first (a previous native capture or loopback track), so
		// switching the source cleanly replaces the audio rather than stacking publications.
		await this.stopScreenShareAudio(room);
		const capture = await createAppAudioTrack(spec);
		if (!capture) {
			logger.warn('Native stream audio unavailable; screen share will have no audio', {spec});
			return;
		}
		try {
			await participant.publishTrack(capture.track, {
				source: Track.Source.ScreenShareAudio,
				name: 'screen_share_audio',
				dtx: false,
				red: true,
				audioPreset: {maxBitrate: 256_000},
			});
			this.appAudioCapture = capture;
			logger.info('Published native screen share audio', {spec});
		} catch (error) {
			logger.error('Failed to publish native screen share audio', {spec, error});
			capture.stop();
		}
	}

	// Re-apply the stream audio to match the current "Share Stream Audio" + audio-source settings while a
	// share is live — lets the user switch System <-> App (or toggle audio) without restarting the stream.
	// Native path only (Windows desktop); elsewhere the change takes effect on the next share.
	async reapplyStreamAudio(room: Room | null): Promise<void> {
		if (Platform.OS !== 'web') {
			return;
		}
		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) {
			return;
		}
		if (!LocalVoiceStateStore.getSelfStreamAudio()) {
			await this.stopScreenShareAudio(room);
			return;
		}
		const spec = this.computeAudioSpec(this.activeSharedWindowProcessId);
		if (!spec) {
			// No native capture available (web / older desktop) — the setting applies on the next share.
			return;
		}
		await this.publishNativeAudio(room, spec);
	}

	private stopAppAudioCapture(): void {
		if (this.appAudioCapture) {
			this.appAudioCapture.stop();
			this.appAudioCapture = null;
		}
	}

	// Called from the LocalTrackUnpublished(ScreenShare) room event. LiveKit unpublishes the screen-share
	// VIDEO track on its own when the captured window/source closes (or the browser "Stop sharing" button
	// is pressed), but it does NOT tear down the separate ScreenShareAudio track. Finish the teardown here
	// so the stream ends FULLY — including audio.
	async handleScreenShareVideoUnpublished(room: Room | null): Promise<void> {
		if (Platform.OS !== 'web') {
			return;
		}
		// Always make sure no audio is left playing for viewers.
		await this.stopScreenShareAudio(room);
		this.activeSharedWindowProcessId = null;

		// If this unpublish came from an explicit "Stop Streaming" (setScreenShareEnabled is in flight),
		// that path already plays the stop sound and clears the viewer key — don't double up.
		if (this.isScreenSharePending) {
			return;
		}

		// Unexpected end (the shared window was closed): do the rest of what a manual stop would do.
		try {
			this.syncLocalStreamWatchState(false);
		} catch (error) {
			logger.warn('Failed to clear viewer stream key after unexpected screen share end', {error});
		}
		SoundActionCreators.playSound(SoundType.ScreenShareStop);
		logger.info('Screen share source closed — stream fully torn down (including audio)');
	}

	async toggleScreenShareFromKeybind(room: Room | null): Promise<void> {
		const current = LocalVoiceStateStore.getSelfStream();
		await this.setScreenShareEnabled(room, !current);
	}

	resetStreamTracking(): void {
		this.stopAppAudioCapture();
		this.pendingNativeAudio = null;
		this.activeSharedWindowProcessId = null;
		runInAction(() => {
			this.isScreenSharePending = false;
		});
	}
}

export default new VoiceScreenShareManager();
